// React Query hooks for Virtual Buyer data. VB-0 ships the buyer reads used by
// the overview empty state to prove the data layer end-to-end; later stories
// extend `vbKeys` + add mutation hooks.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { vbClient } from "@/services/vb/vbClient";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import type {
  Big5Category,
  Buyer,
  CompetitorInput,
  CreateBuyerPayload,
  SuggestDraft,
} from "@/types/vb";
import type { AnalyticsSource } from "@/types/analytics";
import {
  trackVirtualBuyerQuestionsSaved,
  trackVirtualBuyerRunStarted,
} from "@/lib/posthog";

/** Centralized query-key factory so later stories invalidate consistently. */
export const vbKeys = {
  all: ["vb"] as const,
  buyers: (companyId?: number) =>
    companyId != null
      ? ([...vbKeys.all, "buyers", { company: companyId }] as const)
      : ([...vbKeys.all, "buyers"] as const),
  buyer: (id: number) => [...vbKeys.all, "buyer", id] as const,
  questions: (buyerId: number) => [...vbKeys.all, "questions", buyerId] as const,
  analyses: (buyerId: number) => [...vbKeys.all, "analyses", buyerId] as const,
  analysis: (buyerId: number, analysisId: number) =>
    [...vbKeys.all, "analysis", buyerId, analysisId] as const,
  reportSources: (reportId: number) =>
    [...vbKeys.all, "report-sources", reportId] as const,
  reportSource: (reportId: number, sourceId: string) =>
    [...vbKeys.all, "report-source", reportId, sourceId] as const,
  reportPeriods: (projectId: string) =>
    [...vbKeys.all, "report-periods", projectId] as const,
};

/** Best-effort current buyer total from the React Query cache, for the
 *  `virtual_buyer_count` person property. Takes the largest cached buyers list
 *  (the workspace-wide list when present, else a company-scoped subset) so it
 *  approximates the true total without an extra fetch. Callers clamp to ≥1 at
 *  finalization since the buyer being finalized always exists. */
export function countBuyersInCache(qc: QueryClient): number {
  // `vbKeys.buyers()` (no company) is the ["vb","buyers"] prefix; getQueriesData
  // partial-matches it, so both the workspace-wide and company-scoped lists hit.
  const lists = qc.getQueriesData<Buyer[]>({ queryKey: vbKeys.buyers() });
  let max = 0;
  for (const [, data] of lists) {
    if (Array.isArray(data)) max = Math.max(max, data.length);
  }
  return max;
}

/** Workspace scope for VB reads.
 *
 *  Two jobs:
 *  - `wsReady` gates queries until WorkspaceContext finished resolving. The
 *    axios interceptor only injects X-Workspace-ID after the context calls
 *    `setActiveWorkspaceId`; firing earlier would hit the backend's
 *    personal-workspace fallback and cache another workspace's data.
 *  - `wsKey` is appended to each read query key (suffix, so the prefix-based
 *    `vbKeys.*` invalidations keep matching) so switching workspaces gets its
 *    own cache entries instead of flashing the previous workspace's rows. */
export function useVbWorkspaceScope() {
  const { activeWorkspace, loading } = useWorkspace();
  return { wsKey: activeWorkspace?.id ?? null, wsReady: !loading };
}

/** List all buyers in the caller's org.
 *
 *  Gated on `token`: on a cold page load the auth bootstrap (refresh → set the
 *  axios Authorization header) is async, so firing immediately races it and
 *  yields a 401. Staying disabled until the token is present keeps the query in
 *  the pending state (skeleton) until it can actually authenticate. */
export function useBuyers(companyId?: number, opts?: { enabled?: boolean }) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  return useQuery({
    queryKey: [...vbKeys.buyers(companyId), { ws: wsKey }],
    queryFn: () => vbClient.listBuyers(token, companyId),
    // `opts.enabled` lets a caller that has no company yet skip the request
    // entirely rather than fetching the workspace-wide list and discarding it
    // (same convention as `useBuyer`'s `valid` gate below). The query KEY is
    // unchanged, so callers asking for the same company still share one fetch.
    enabled: !!token && wsReady && (opts?.enabled ?? true),
  });
}

/** Buyers one workspace may hold for ONE company, mirroring the backend's
 *  `Buyer.MAX_PER_COMPANY`. Per company, not per workspace: filling this company
 *  up leaves the workspace's other companies with their own five. */
export const MAX_BUYERS_PER_COMPANY = 5;

/** How much of `companyId`'s buyer allowance is used (AITS-417).
 *
 *  Counts the company-scoped buyers list rather than adding an endpoint. On the
 *  overview that is free — the page already runs the identical query, so both
 *  subscribers share one fetch. The wizard doesn't otherwise list buyers, so
 *  there it costs one small GET. `POST /api/buyers/` is the real enforcement;
 *  this only decides whether the UI offers the action. */
export function useCompanyBuyerCap(companyId: number | undefined) {
  const scoped = companyId != null;
  const { data, isPending } = useBuyers(companyId, { enabled: scoped });
  const known = scoped && !isPending && Array.isArray(data);
  const used = known ? data.length : 0;
  return {
    max: MAX_BUYERS_PER_COMPANY,
    /** Real count, which CAN exceed `max` — buyers predate the cap. 0 until known. */
    used,
    /** False while the count is still loading. Gate on this before rendering
     *  copy about the number, or telling a user their company is full. */
    known,
    atCap: known && used >= MAX_BUYERS_PER_COMPANY,
    /** How many must go before one can be added. Mirrors the backend's
     *  `surplus` (BuyerListCreateView.create): at 8 of 5, "delete one" is
     *  wrong — they need to delete 4. */
    mustDelete: Math.max(1, used - MAX_BUYERS_PER_COMPANY + 1),
  };
}

/** Fetch a single buyer. Accepts a possibly-undefined/NaN id (callers typically
 *  pass `Number(useParams().buyerId)`); the query stays disabled until it's a
 *  valid positive integer and the user is authenticated. */
export function useBuyer(buyerId: number | undefined) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  const valid = buyerId != null && Number.isInteger(buyerId) && buyerId > 0;
  return useQuery({
    queryKey: [...vbKeys.buyer(buyerId ?? -1), { ws: wsKey }],
    queryFn: () => vbClient.getBuyer(buyerId as number, token),
    enabled: valid && !!token && wsReady,
  });
}

/** List a buyer's analysis shadow rows, newest-first. Powers the detail page's
 *  history table and locating the latest DONE run for the Big-5 join. */
export function useAnalyses(buyerId: number | undefined) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  const valid = buyerId != null && Number.isInteger(buyerId) && buyerId > 0;
  return useQuery({
    queryKey: [...vbKeys.analyses(buyerId ?? -1), { ws: wsKey }],
    queryFn: () => vbClient.listAnalyses(buyerId as number, token),
    enabled: valid && !!token && wsReady,
  });
}

/** Fetch one analysis (shadow + live progress or final result). Stays disabled
 *  until both ids are valid - the detail page only resolves the latest DONE id
 *  after the analyses list loads.
 *
 *  `poll`: while the analysis is non-terminal, refetch on an interval. The GET
 *  reconciles the Django shadow with Jarts (advances PENDING→IN_PROGRESS→DONE),
 *  so the run-progress screen (VB-E) and the detail page advance live instead of
 *  sitting at "Queued". Polling pauses when the tab is unfocused (React Query's
 *  refetchIntervalInBackground default), which is fine - it resumes on focus. */
export function useAnalysis(
  buyerId: number | undefined,
  analysisId: number | undefined,
  opts?: { poll?: boolean },
) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  const valid =
    buyerId != null && Number.isInteger(buyerId) && buyerId > 0 &&
    analysisId != null && Number.isInteger(analysisId) && analysisId > 0;
  return useQuery({
    queryKey: [...vbKeys.analysis(buyerId ?? -1, analysisId ?? -1), { ws: wsKey }],
    queryFn: () => vbClient.getAnalysis(buyerId as number, analysisId as number, token),
    enabled: valid && !!token && wsReady,
    refetchInterval: opts?.poll
      ? (query) => {
          const s = (query.state.data as { status?: string } | undefined)?.status;
          return s === "DONE" || s === "FAILED" ? false : 4000;
        }
      : false,
  });
}

/** List a buyer's questions. The edit drawer and Big-5 coverage card consume
 *  this; question mutations invalidate `vbKeys.all` so it refetches. */
export function useQuestions(buyerId: number | undefined) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  const valid = buyerId != null && Number.isInteger(buyerId) && buyerId > 0;
  return useQuery({
    queryKey: [...vbKeys.questions(buyerId ?? -1), { ws: wsKey }],
    queryFn: () => vbClient.listQuestions(buyerId as number, token),
    enabled: valid && !!token && wsReady,
  });
}

// ---------------------------------------------------------------------------
// Mutations (VB-B1 + VB-B2). Thin wrappers over `vbClient`, each injecting the
// per-request token. The wizard composes them via `mutateAsync` and keeps its
// own local `questions[]` mirror; the detail-view edit drawer instead relies on
// the `vbKeys.all` invalidation below to refetch the questions list + buyer.
// ---------------------------------------------------------------------------

/** Ensure the buyer-side company-context bundle exists for `companyId` before
 *  the wizard lets the user do anything (the Suggest / Generate steps need it).
 *  Runs once on wizard entry; the wizard renders a loading state until this
 *  settles. `staleTime: Infinity` so re-entry within the session is instant; we
 *  drive our own retry, so `retry: false`. */
export function useEnsureCompanyContext(companyId: number | undefined) {
  const { token } = useAuth();
  const valid = companyId != null && Number.isInteger(companyId) && companyId > 0;
  return useQuery({
    queryKey: [...vbKeys.all, "company-context", companyId ?? -1],
    queryFn: () => vbClient.ensureCompanyContext(companyId as number, token),
    enabled: valid && !!token,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

/** LLM persona suggestion for a company (no persist). An optional `draft`
 *  biases the suggestion toward the buyer the user is already building. */
export function useSuggestBuyer() {
  const { token } = useAuth();
  return useMutation({
    mutationFn: (vars: { companyId: number; draft?: SuggestDraft }) =>
      vbClient.suggestBuyer(vars.companyId, token, vars.draft),
  });
}

/** Create a buyer; invalidate the overview list so it reflects the new row. */
export function useCreateBuyer(source?: AnalyticsSource) {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBuyerPayload) => vbClient.createBuyer(payload, token, source),
    onSuccess: () => {
      // `virtual_buyer_created` is NOT emitted here: the buyer is created lazily
      // (on "Generate questions"), before the user finishes editing the persona
      // and questions. It fires at finalization (first run) instead, where
      // buyer_origin can reflect those edits (AITS-545 QA).
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Partial update of a buyer's scalar persona fields (back-nav edits). */
export function usePatchBuyer() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      buyerId,
      partial,
    }: {
      buyerId: number;
      partial: Partial<Pick<Buyer, "name" | "description" | "market_region" | "market_city" | "language">>;
    }) => vbClient.patchBuyer(buyerId, partial, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Generate the Big-5 set (replace-all, 10 questions). Invalidates so the edit
 *  drawer's questions list + Big-5 denominators refetch (the wizard ignores its
 *  own query cache and keeps a local mirror, so this is a no-op there). */
export function useGenerateQuestions(source?: AnalyticsSource) {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buyerId: number) => vbClient.generateQuestions(buyerId, token, source),
    onSuccess: (questions, buyerId) => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
      trackVirtualBuyerQuestionsSaved({
        buyerId,
        questionsCount: questions.length,
        source,
      });
    },
  });
}

/** Manually add a question (source MANUAL). */
export function useAddQuestion() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      buyerId,
      category,
      text,
    }: {
      buyerId: number;
      category: Big5Category;
      text: string;
    }) => vbClient.addQuestion(buyerId, { category, text }, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Edit a question's text (source flips GENERATED→EDITED). */
export function useUpdateQuestion() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, text }: { questionId: string; text: string }) =>
      vbClient.updateQuestion(questionId, text, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Delete a question. */
export function useDeleteQuestion() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => vbClient.deleteQuestion(questionId, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Add a competitor (post-create live CRUD). Invalidates so the overview /
 *  buyer-detail caches don't show stale competitor data after a back-nav edit. */
export function useAddCompetitor() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ buyerId, competitor }: { buyerId: number; competitor: CompetitorInput }) =>
      vbClient.addCompetitor(buyerId, competitor, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Delete a competitor (post-create live CRUD). */
export function useDeleteCompetitor() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (competitorId: number) => vbClient.deleteCompetitor(competitorId, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}

/** Trigger the first analysis run for a buyer. `questionsCount` is analytics-only
 *  (it rides the `virtual_buyer_run_started` event, not the request body). */
export function useTriggerAnalysis(source?: AnalyticsSource) {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { buyerId: number; questionsCount?: number }) =>
      vbClient.triggerAnalysis(vars.buyerId, token, source),
    onSuccess: (_shadow, vars) => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
      trackVirtualBuyerRunStarted({
        buyerId: vars.buyerId,
        questionsCount: vars.questionsCount,
        source,
      });
    },
  });
}

/** Best-effort buyer delete (orphan cleanup on wizard cancel). */
export function useDeleteBuyer() {
  const { token } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buyerId: number) => vbClient.deleteBuyer(buyerId, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vbKeys.all });
    },
  });
}
